import { Outlet } from 'react-router';

import { useAppDispatch, useAppSelector } from '@app/hooks';
import { useLogoutMutation } from '@features/auth';
import {
  NotificationBell,
  selectFullscreen,
  selectRailCollapsed,
  Sidebar,
  toggleRail,
  YearPicker,
} from '@features/shell';
import { BaseButton, BaseDialog, BaseIconButton, confirm, useToast } from '@shared/components/base';
import { useIdleTimer } from '@shared/hooks/useIdleTimer';
import { selectPages } from '@shared/session/sessionSlice';

import { APP_TEXT } from '../constants';
import { RealtimeBridge } from '../realtime/RealtimeBridge';

/** Sign-out after 60 idle minutes, with a one-minute warning (plan D16). */
export const IDLE_TIMEOUT_MS = 60 * 60_000;
const IDLE_WARNING_MS = 60_000;

/** Signed-in frame: collapsible left rail + main content area. */
export function AppLayout() {
  const railCollapsed = useAppSelector(selectRailCollapsed);
  const fullscreen = useAppSelector(selectFullscreen);
  const pages = useAppSelector(selectPages);
  const dispatch = useAppDispatch();
  const toast = useToast();
  const [logout] = useLogoutMutation();
  const signOut = () => void logout(undefined);
  // Sidebar "Sign out": asks first, then confirms it with a toast (seen on the sign-in page).
  const confirmSignOut = async () => {
    const ok = await confirm({
      header: APP_TEXT.layout.signOutTitle,
      message: APP_TEXT.layout.signOutMessage,
      acceptLabel: APP_TEXT.layout.signOut,
    });
    if (!ok) return;
    await logout(undefined);
    toast.success(APP_TEXT.layout.signedOut);
  };
  const idle = useIdleTimer({ timeoutMs: IDLE_TIMEOUT_MS, warnMs: IDLE_WARNING_MS, onTimeout: signOut });

  const idleDialog = (
    <BaseDialog
      visible={idle.warning}
      onHide={idle.reset}
      header={APP_TEXT.layout.idleTitle}
      width="sm"
      footer={
        <>
          <BaseButton variant="secondary" onClick={signOut}>
            {APP_TEXT.layout.signOut}
          </BaseButton>
          <BaseButton onClick={idle.reset}>{APP_TEXT.layout.staySignedIn}</BaseButton>
        </>
      }
    >
      <p className="text-[13px] normal-case">{APP_TEXT.layout.idleMessage}</p>
    </BaseDialog>
  );

  // Inbox tabs run full screen: no sidebar, no masthead, no page padding (prototype). The
  // tree keeps the same shape either way so the page underneath never remounts.
  const rail = !railCollapsed && !fullscreen;
  return (
    <div className={rail ? 'grid min-h-screen grid-cols-[214px_minmax(0,1fr)]' : 'block min-h-screen'}>
      <RealtimeBridge />
      {rail && <Sidebar onSignOut={() => void confirmSignOut()} />}
      {railCollapsed && !fullscreen && (
        <BaseIconButton
          icon="pi pi-bars"
          size="sm"
          round
          onClick={() => dispatch(toggleRail())}
          className="fixed top-4.5 left-4.5 z-30 shadow-app"
          aria-label={APP_TEXT.layout.showSidebar}
          title={APP_TEXT.layout.showSidebar}
        />
      )}
      <main
        className={
          fullscreen
            ? 'min-h-screen min-w-0'
            : railCollapsed
              ? 'min-w-0 px-6.5 pt-15.5 pb-14'
              : 'min-w-0 px-6.5 pt-5.5 pb-14'
        }
      >
        {/* The year in use, read-only (it is changed in Settings), and the notification bell. */}
        {!fullscreen && (
          <div className="mb-4 flex items-center justify-end gap-3">
            <YearPicker locked />
            {pages.notifs && <NotificationBell />}
          </div>
        )}
        <Outlet />
      </main>

      {idleDialog}
    </div>
  );
}
